import { useState, type FormEvent } from 'react';
import type { PhonebookCompany } from '@arcnet/contracts';
import { ApiClientError } from '../../lib/api/client.js';
import { useCompanies, useDeleteCompany, usePhonebookAccess, useSaveCompany } from '../../lib/api/hooks-phonebook.js';
import { Empty, ErrorBanner, SearchInput, SectionHead, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Companies in the account's phonebook (docs/32): the organisations its contacts work at, with
 * a main number the phones can list too. The client's only.
 */
export function CompaniesPage({ tenantId }: { tenantId: string }) {
  const access = usePhonebookAccess(tenantId);
  const [q, setQ] = useState('');
  const companies = useCompanies(tenantId, q.trim());
  const remove = useDeleteCompany(tenantId);
  const [editing, setEditing] = useState<PhonebookCompany | 'new' | null>(null);
  const mayWrite = access.data?.can_write ?? false;
  const rows = companies.data?.data ?? [];

  if (access.error) return <ErrorBanner error={access.error} />;

  return (
    <>
      <SectionHead title="Companies" sub="The organisations your contacts work at."
        action={mayWrite && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            onClick={() => setEditing(editing === 'new' ? null : 'new')}>
            <Icon name={editing === 'new' ? 'close' : 'plus'} size={13} />
            {editing === 'new' ? 'Cancel' : 'Add company'}
          </button>
        )} />

      {editing !== null && (
        <CompanyEditor key={editing === 'new' ? 'new' : editing.id} tenantId={tenantId}
          company={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      )}
      {(companies.error || remove.error) && <ErrorBanner error={companies.error ?? remove.error} />}

      <div className="arc-card">
        <div className="arc-card-head">
          <SearchInput value={q} onChange={setQ} placeholder="Search by name or number" label="Search companies" />
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Main number</th><th>Email</th><th>Website</th>
                <th className="arc-td-right">Contacts</th><th />
              </tr>
            </thead>
            {companies.data === undefined ? <TableSkeleton cols={6} /> : (
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 550 }}>{c.name}</td>
                    <td className="arc-mono">{c.phone ?? ''}</td>
                    <td>{c.email ?? ''}</td>
                    <td className="arc-muted">{c.website ?? ''}</td>
                    <td className="arc-td-right arc-num">{c.contacts}</td>
                    <td className="arc-td-right">
                      {mayWrite && (
                        <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setEditing(c)}>
                            Edit
                          </button>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={remove.isPending}
                            onClick={() => {
                              if (confirm(`Delete ${c.name}? Its contacts stay, without a company.`)) remove.mutate(c.id);
                            }}>
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {companies.data !== undefined && rows.length === 0 && (
          <Empty icon="accounts" title={q ? 'No companies match' : 'No companies'}
            hint={q ? 'Try another name or number.' : 'Add the organisations your contacts work at.'} />
        )}
      </div>
    </>
  );
}

function CompanyEditor({ tenantId, company, onDone }: {
  tenantId: string; company: PhonebookCompany | null; onDone: () => void;
}) {
  const save = useSaveCompany(tenantId);
  const [name, setName] = useState(company?.name ?? '');
  const [phone, setPhone] = useState(company?.phone ?? '');
  const [email, setEmail] = useState(company?.email ?? '');
  const [website, setWebsite] = useState(company?.website ?? '');
  const [notes, setNotes] = useState(company?.notes ?? '');
  const errors = save.error instanceof ApiClientError ? save.error.fieldErrors : {};

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      { ...(company ? { id: company.id } : {}), name, phone, email, website, notes },
      { onSuccess: onDone },
    );
  };

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="co-name">Name</label>
          <input id="co-name" className="arc-input" required autoFocus maxLength={120}
            value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Logistics" />
          {errors['name'] && <div className="arc-error">{errors['name']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="co-phone">Main number</label>
          <input id="co-phone" className="arc-input arc-mono" inputMode="tel" maxLength={30}
            value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="021 555 0100" />
          {errors['phone'] && <div className="arc-error">{errors['phone']}</div>}
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="co-email">Email</label>
          <input id="co-email" className="arc-input" type="email" maxLength={254}
            value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="co-web">Website</label>
          <input id="co-web" className="arc-input" maxLength={200}
            value={website} onChange={(e) => setWebsite(e.target.value)} />
        </div>
      </div>
      <div className="arc-field" style={{ margin: '12px 0 0' }}>
        <label className="arc-label" htmlFor="co-notes">Notes</label>
        <textarea id="co-notes" className="arc-input" rows={2} maxLength={2000}
          value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      {save.error && Object.keys(errors).length === 0 && <ErrorBanner error={save.error} />}
      <div className="arc-row" style={{ gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !name.trim()}>
          {save.isPending ? 'Saving…' : company ? 'Save company' : 'Add company'}
        </button>
      </div>
    </form>
  );
}
